import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Card, Switch, Space, InputNumber, Typography, message, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, TeamOutlined } from '@ant-design/icons';
import { teamAPI } from '../services/api';

const { Title } = Typography;
const { TextArea } = Input;

export default function TeamForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchTeamMember = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            const response = await teamAPI.getById(id);
            form.setFieldsValue(response.data);
        } catch (error) {
            console.error('Error fetching team member:', error);
            message.error('Failed to fetch team member');
        } finally {
            setLoading(false);
        }
    }, [id, form]);

    useEffect(() => {
        if (isEdit) {
            fetchTeamMember();
        }
    }, [isEdit, fetchTeamMember]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            if (isEdit) {
                await teamAPI.update(id, values);
                message.success('Team member updated!');
            } else {
                await teamAPI.create(values);
                message.success('Team member added!');
            }
            navigate('/team');
        } catch (error) {
            message.error('Error: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/team')} style={{ marginBottom: 16 }}>
                    Back to Team
                </Button>
                <Title level={2}><TeamOutlined /> {isEdit ? 'Edit' : 'Add'} Team Member</Title>
            </div>

            <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ order: 0, isActive: true }}>
                    <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
                        <Input size="large" placeholder="John Doe" />
                    </Form.Item>
                    <Form.Item name="position" label="Position" rules={[{ required: true }]}>
                        <Input size="large" placeholder="Senior Engineer" />
                    </Form.Item>
                    <Form.Item name="department" label="Department">
                        <Input size="large" placeholder="Engineering" />
                    </Form.Item>
                    <Form.Item name="photo" label="Photo URL">
                        <Input size="large" placeholder="https://example.com/photo.jpg" />
                    </Form.Item>
                    <Form.Item name="bio" label="Biography">
                        <TextArea rows={4} placeholder="Brief bio..." />
                    </Form.Item>
                    <Form.Item name="email" label="Email">
                        <Input size="large" type="email" placeholder="john@company.com" />
                    </Form.Item>
                    <Form.Item name="phone" label="Phone">
                        <Input size="large" placeholder="+62 812 3456 7890" />
                    </Form.Item>
                    <Form.Item name="linkedin" label="LinkedIn URL">
                        <Input size="large" placeholder="https://linkedin.com/in/username" />
                    </Form.Item>
                    <Form.Item name="order" label="Display Order">
                        <InputNumber size="large" min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="isActive" label="Status" valuePropName="checked">
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>
                    <Form.Item style={{ marginTop: 32 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button size="large" onClick={() => navigate('/team')} style={{ minWidth: 120 }}>Cancel</Button>
                            <Button type="primary" size="large" htmlType="submit" icon={<SaveOutlined />} loading={submitting} style={{ minWidth: 120, background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', border: 'none' }}>
                                {isEdit ? 'Update' : 'Add'} Member
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
}
